import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  DollarSign,
  Users,
  Briefcase,
  TrendingUp,
  AlertTriangle,
  Shield,
  MessageSquare,
  FileText,
  Bell,
  UserPlus,
  Upload,
  RefreshCw,
  ArrowRight,
  User,
  Clock,
  Handshake,
  ArrowDownCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import InvestecWidget from '../../components/widgets/InvestecWidget';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const MetricCard = ({ title, value, icon: Icon, trend, description, color = "amber" }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {description && (
            <p className="text-xs text-gray-400 mt-1">{description}</p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
      {trend && (
        <div className={`mt-3 flex items-center gap-1 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          <TrendingUp className={`h-3 w-3 ${trend < 0 ? 'rotate-180' : ''}`} />
          <span>{Math.abs(trend)}% from last month</span>
        </div>
      )}
    </CardContent>
    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-${color}-500`} />
  </Card>
);

const AlertCard = ({ alert, onClick }) => {
  const typeConfig = {
    danger: { bg: 'bg-red-50', border: 'border-red-200', icon: AlertTriangle, iconColor: 'text-red-500' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-500' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: MessageSquare, iconColor: 'text-blue-500' }
  };
  
  const config = typeConfig[alert.type] || typeConfig.info;
  const IconComponent = config.icon;
  
  return (
    <div 
      className={`p-4 rounded-lg border ${config.bg} ${config.border} cursor-pointer hover:shadow-md transition-shadow`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <IconComponent className={`h-5 w-5 flex-shrink-0 ${config.iconColor}`} />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">{alert.title}</h4>
            <Badge variant="secondary" className="ml-2">{alert.count}</Badge>
          </div>
          <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
        </div>
        <ArrowRight className="h-5 w-5 text-gray-400" />
      </div>
    </div>
  );
};

const ActivityItem = ({ activity }) => {
  const iconConfig = {
    login: { icon: User, color: 'text-blue-500', bg: 'bg-blue-100' },
    kyc: { icon: Shield, color: 'text-green-500', bg: 'bg-green-100' },
    support: { icon: MessageSquare, color: 'text-purple-500', bg: 'bg-purple-100' },
    eoi: { icon: Handshake, color: 'text-amber-500', bg: 'bg-amber-100' }
  };
  
  const config = iconConfig[activity.type] || iconConfig.login;
  const IconComponent = config.icon;
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };
  
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.bg}`}>
        <IconComponent className={`h-4 w-4 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 truncate">{activity.message}</p>
        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
          <Clock className="h-3 w-3" />
          {formatTime(activity.timestamp)}
        </p>
      </div>
    </div>
  );
};

export default function AdminDashboardHome() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [activities, setActivities] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [metricsRes, activityRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard/metrics`, { headers }),
        axios.get(`${API_URL}/api/admin/dashboard/activity`, { headers }),
        axios.get(`${API_URL}/api/admin/dashboard/alerts`, { headers })
      ]);
      
      setMetrics(metricsRes.data);
      setActivities(activityRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-dashboard-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-96 lg:col-span-2" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-500 mt-1">Overview of your portfolio and operations</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          className="flex items-center gap-2"
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total AUM"
          value={formatCurrency(metrics?.total_aum || 0)}
          icon={DollarSign}
          color="amber"
          description={`Loans: ${formatCurrency(metrics?.total_loan_balances || 0)} | Cash: ${formatCurrency((metrics?.investor_cash_balances || 0) + (metrics?.main_account_balance || 0))}`}
        />
        <MetricCard
          title="Active Investors"
          value={metrics?.active_investors || 0}
          icon={Users}
          color="blue"
        />
        <MetricCard
          title="Active Loans"
          value={metrics?.active_loans || 0}
          icon={Briefcase}
          color="green"
          description={`${metrics?.active_tranches || 0} tranches`}
        />
        <MetricCard
          title="Portfolio Yield"
          value={`${metrics?.portfolio_yield?.toFixed(1) || 0}%`}
          icon={TrendingUp}
          color="purple"
          description="Weighted average rate"
        />
      </div>

      {/* Ledger vs Investec Reconciliation Strip */}
      {(metrics?.investor_cash_balances !== undefined) && (
        <Card data-testid="ledger-recon-strip" className={Math.abs(metrics?.investor_cash_recon_gap || 0) > 1 ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}>
          <CardContent className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-3 text-sm">
              <div className="flex items-center gap-2 font-medium">
                {Math.abs(metrics?.investor_cash_recon_gap || 0) > 1 ? (
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                ) : (
                  <Shield className="h-4 w-4 text-emerald-600" />
                )}
                <span>Investor Cash — Ledger vs Investec Live</span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-slate-500">Ledger total:</span>{' '}
                  <span className="font-mono font-semibold" data-testid="ledger-total">{formatCurrency(metrics?.investor_cash_balances || 0)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Investec live:</span>{' '}
                  <span className="font-mono font-semibold" data-testid="investec-live-total">{formatCurrency(metrics?.investor_cash_investec_live || 0)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Gap (bank − ledger):</span>{' '}
                  <span
                    className={`font-mono font-bold ${Math.abs(metrics?.investor_cash_recon_gap || 0) > 1 ? 'text-amber-700' : 'text-emerald-700'}`}
                    data-testid="ledger-recon-gap"
                  >
                    {formatCurrency(metrics?.investor_cash_recon_gap || 0)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Second Row Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="PAR30 Ratio"
          value={`${metrics?.par30_ratio?.toFixed(1) || 0}%`}
          icon={AlertTriangle}
          color="red"
          description="Loans marked in default"
        />
        <MetricCard
          title="Pending KYC"
          value={metrics?.pending_kyc_reviews || 0}
          icon={Shield}
          color="orange"
        />
        <MetricCard
          title="Pending Deployments"
          value={metrics?.pending_deployments || 0}
          icon={ArrowDownCircle}
          color="indigo"
          description="Future-dated deals approved"
        />
        <MetricCard
          title="MTD Collections"
          value={formatCurrency(metrics?.mtd_collections || 0)}
          icon={DollarSign}
          color="teal"
          description="Repayments this month"
        />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Investec Widget */}
        <InvestecWidget onNavigate={navigate} />
        
        {/* Alerts */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Alerts
            </CardTitle>
            <CardDescription>Items needing attention</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 max-h-96 overflow-y-auto">
            {alerts.length > 0 ? (
              alerts.map((alert, index) => (
                <AlertCard 
                  key={index} 
                  alert={alert} 
                  onClick={() => navigate(alert.link)}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Shield className="h-12 w-12 mx-auto text-green-300 mb-2" />
                <p>All clear!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Second Row - Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-500" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-80 overflow-y-auto">
            {activities.length > 0 ? (
              activities.slice(0, 10).map((activity, index) => (
                <ActivityItem key={index} activity={activity} />
              ))
            ) : (
              <p className="text-center text-gray-500 py-4">No recent activity</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
          <CardDescription>Common administrative tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/admin/statements')}
              data-testid="quick-action-statement"
            >
              <Upload className="h-6 w-6 text-blue-500" />
              <span className="text-sm">Upload Statement</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/admin/notices')}
              data-testid="quick-action-notice"
            >
              <Bell className="h-6 w-6 text-purple-500" />
              <span className="text-sm">Create Notice</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/admin/investors')}
              data-testid="quick-action-investor"
            >
              <UserPlus className="h-6 w-6 text-green-500" />
              <span className="text-sm">Add Investor</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/admin/distributions')}
              data-testid="quick-action-distribution"
            >
              <DollarSign className="h-6 w-6 text-amber-500" />
              <span className="text-sm">Record Distribution</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
